import React from "react"
import { useQuery, useQueryClient } from "react-query"
import { toast } from "react-toastify"
import Swal from "sweetalert2"
import moment from "moment"
import { Link, useNavigate } from "react-router-dom"
import { useAuth } from "../../../AppContext"
import {
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_CREATE,
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_FINALISER,
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DETAILS,
     ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_EDITER,
} from "../../gestion/routing/routing-endpoints"
import {
     CONST_USER_PERMISSION_ADMINISTRATION_STRUCTURE_GESTION_STAFF_ADD_VIEW,
     CONST_USER_ROLE_DAF,
     CONST_USER_ROLE_DG,
     CONST_USER_ROLE_RESPONSABLE_RH,
     CONST_USER_ROLE_RH,
     CONST_USER_ROLE_SUPER_ADMIN,
} from "@common-constants/*"
import { IPersonnelDemandeEmbaucheModel } from "../core/_models"
import {
     personnelDEListingRequest,
     personnelDEGetDetailsRequest,
     personnelDESoumettreRequest,
     personnelDEValiderDgRequest,
     personnelDERectifierDgRequest,
     personnelDEValiderHrRequest,
     personnelDERectifierHrRequest,
     personnelDESupprimerRequest,
} from "../core/_requests"
import MyModal from "@common-utils/MyModal"
import CenteredLoader from "./_CenteredLoader"
import { formatPrenomNom } from "../core/_nameFormat"
import { driveImageUrl } from "../core/_avatar"
import "./demande-embauche-listing.css"

// États (miroir du back)
const ETAT_BROUILLON = "ETAT_BROUILLON"
const ETAT_SOUMIS_DG = "ETAT_SOUMIS_DG"
const ETAT_VALIDE_DG = "ETAT_VALIDE_DG"
const ETAT_RECTIF_PAR_DG = "ETAT_RECTIF_PAR_DG"
const ETAT_RECTIF_PAR_HR = "ETAT_RECTIF_PAR_HR"
const ETAT_VALIDE_HR = "ETAT_VALIDE_HR"
const ETAT_FINALISE = "ETAT_FINALISE"

// Couleur contextuelle par état (badge + accent de la carte)
const ETAT_COLOR: Record<string, string> = {
     [ETAT_BROUILLON]: "secondary",
     [ETAT_SOUMIS_DG]: "warning",
     [ETAT_VALIDE_DG]: "info",
     [ETAT_RECTIF_PAR_DG]: "danger",
     [ETAT_RECTIF_PAR_HR]: "danger",
     [ETAT_VALIDE_HR]: "primary",
     [ETAT_FINALISE]: "success",
}

const REACT_QUERY_KEY_DE_STAFF_LISTING = "REACT_QUERY_KEY_DE_STAFF_LISTING"

function initials(prenom?: string, nom?: string): string {
     const a = (prenom ?? "").trim().charAt(0)
     const b = (nom ?? "").trim().charAt(0)
     return `${a}${b}`.toUpperCase() || "?"
}

const DemandesEmbaucheStaffListing = () => {
     const { currentUser } = useAuth()
     const navigate = useNavigate()
     const queryClient = useQueryClient()
     const roles = currentUser?.roles ?? []

     const isSuperAdmin = roles.includes(CONST_USER_ROLE_SUPER_ADMIN)
     const isDg = roles.includes(CONST_USER_ROLE_DG)
     const isHr = roles.includes(CONST_USER_ROLE_DAF) || roles.includes(CONST_USER_ROLE_RESPONSABLE_RH) || roles.includes(CONST_USER_ROLE_RH)
     // Valider/rectifier DG : uniquement ROLE_DG. Valider/rectifier RH : uniquement DAF / Responsable RH / RH.
     const canActDg = isDg
     const canActHr = isHr
     // Ajout d'un collaborateur : uniquement si l'utilisateur connecté a la permission « Ajouter Staff ».
     const canAdd = !!currentUser?.permissions?.includes(CONST_USER_PERMISSION_ADMINISTRATION_STRUCTURE_GESTION_STAFF_ADD_VIEW)

     const [suiviModal, setSuiviModal] = React.useState<{ show: boolean; demande?: IPersonnelDemandeEmbaucheModel; loading: boolean }>({ show: false, loading: false })

     // Action en cours (id de la demande + clé d'action) : verrouille les autres boutons de la ligne + loader ciblé.
     const [busy, setBusy] = React.useState<{ id: number | null; action: string | null }>({ id: null, action: null })

     const query = useQuery<IPersonnelDemandeEmbaucheModel[]>(REACT_QUERY_KEY_DE_STAFF_LISTING, () => personnelDEListingRequest().then(r => r.data))

     function refresh() {
          return queryClient.invalidateQueries(REACT_QUERY_KEY_DE_STAFF_LISTING)
     }

     function runAction(promise: Promise<any>, successMsg: string, id: number, action: string) {
          setBusy({ id, action })
          toast.info("Traitement en cours…", { autoClose: false })
          promise
               .then(() => {
                    toast.dismiss()
                    toast.success(successMsg)
                    return refresh()
               })
               .catch(e => {
                    toast.dismiss()
                    toast.error(e?.response?.data?.detail ?? "Une erreur est survenue.", { autoClose: false })
               })
               .finally(() => setBusy({ id: null, action: null }))
     }

     function handleSoumettre(d: IPersonnelDemandeEmbaucheModel) {
          const rectif = [ETAT_RECTIF_PAR_DG, ETAT_RECTIF_PAR_HR].includes(d.etat ?? "")
          if (rectif) {
               Swal.fire({
                    icon: "question",
                    title: "Envoyer la demande corrigée ?",
                    input: "textarea",
                    inputLabel: "Commentaire (facultatif)",
                    inputPlaceholder: "Précisez les corrections apportées…",
                    showCancelButton: true,
                    confirmButtonText: "Envoyer",
                    cancelButtonText: "Annuler",
               }).then(res => res.isConfirmed && runAction(personnelDESoumettreRequest(d.id, (res.value ?? "").trim() || undefined), "La demande d'embauche a bien été envoyée", d.id, "soumettre"))
               return
          }
          Swal.fire({
               icon: "question",
               title: "Voulez-vous soumettre la demande d'embauche ?",
               text: "Tous les champs et documents requis doivent être renseignés. La soumission sera bloquée sinon.",
               input: "textarea",
               inputLabel: "Commentaire (facultatif)",
               inputPlaceholder: "Ajouter un commentaire…",
               showCancelButton: true,
               confirmButtonText: "Soumettre",
               cancelButtonText: "Annuler",
          }).then(res => res.isConfirmed && runAction(personnelDESoumettreRequest(d.id, (res.value ?? "").trim() || undefined), "La demande d'embauche a bien été envoyée", d.id, "soumettre"))
     }

     function handleValiderDg(d: IPersonnelDemandeEmbaucheModel) {
          const reexamen = !!d.isDgReexamen
          Swal.fire({
               icon: "question",
               title: reexamen ? "Revalider cette demande (DG) ?" : "Valider cette demande (DG) ?",
               text: reexamen ? "Les RH ont renvoyé cette demande pour un nouvel examen. Elle repartira ensuite vers les RH." : undefined,
               input: "textarea",
               inputLabel: "Commentaire (facultatif)",
               inputPlaceholder: reexamen ? "Précisez votre réponse au renvoi des RH…" : "Ajouter un commentaire…",
               showCancelButton: true,
               confirmButtonText: reexamen ? "Revalider" : "Valider",
               cancelButtonText: "Annuler",
          }).then(res => res.isConfirmed && runAction(personnelDEValiderDgRequest(d.id, (res.value ?? "").trim() || undefined), "Demande validée par la DG.", d.id, "validerDg"))
     }

     function handleValiderHr(d: IPersonnelDemandeEmbaucheModel) {
          const hasContrat = ((d.personnel as any)?.personnelDocuments ?? []).some((doc: any) => doc.type === "TYPE_CONTRAT" && !doc.isDeleted && doc.googleDriveID)
          Swal.fire({
               icon: "question",
               title: "Valider cette demande (RH) ?",
               input: "file",
               inputLabel: hasContrat ? "Remplacer le contrat de travail (facultatif)" : "Contrat de travail (obligatoire)",
               inputAttributes: { accept: "application/pdf,image/*", "aria-label": "Contrat de travail" },
               inputValidator: v => (!v && !hasContrat ? "Le contrat de travail est requis pour valider." : undefined),
               showCancelButton: true,
               confirmButtonText: "Valider",
               cancelButtonText: "Annuler",
          }).then(res => res.isConfirmed && runAction(personnelDEValiderHrRequest(d.id, (res.value as File) || undefined), "Demande validée par les RH.", d.id, "validerHr"))
     }

     function handleRectifierDg(d: IPersonnelDemandeEmbaucheModel) {
          Swal.fire({
               icon: "warning",
               title: "Demander une rectification",
               input: "textarea",
               inputLabel: "Commentaire (obligatoire)",
               inputPlaceholder: "Précisez ce qui doit être corrigé…",
               showCancelButton: true,
               confirmButtonText: "Envoyer",
               cancelButtonText: "Annuler",
               inputValidator: v => (!v || !v.trim() ? "Un commentaire est requis" : undefined),
          }).then(res => res.isConfirmed && res.value && runAction(personnelDERectifierDgRequest(d.id, res.value.trim()), "Rectification demandée.", d.id, "rectifierDg"))
     }

     function handleRectifierHr(d: IPersonnelDemandeEmbaucheModel) {
          const creatorName = (formatPrenomNom(d.requester?.prenom, d.requester?.nom) || "au créateur").replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c] as string))
          // Créateur DG : la demande n'est jamais passée par la DG → la rectification revient toujours au créateur
          // (pas de choix créateur/DG), on affiche simplement le destinataire.
          const cibleChoixHtml = d.creatorIsDg
               ? `<div class="text-muted text-start fs-6">La demande sera envoyée à <b>${creatorName}</b>.</div>`
               : `<select id="de-rectif-cible" class="form-select"><option value="creator">Renvoyer à ${creatorName}</option><option value="dg">Renvoyer à la DG</option></select>`
          Swal.fire({
               icon: "warning",
               title: "Demander une rectification",
               html: `<textarea id="de-rectif-comment" class="form-control mb-3" style="min-height:130px" placeholder="Commentaire (obligatoire)…"></textarea>` + cibleChoixHtml,
               showCancelButton: true,
               confirmButtonText: "Envoyer",
               cancelButtonText: "Annuler",
               didOpen: () => (document.getElementById("de-rectif-comment") as HTMLTextAreaElement)?.focus(),
               preConfirm: () => {
                    const comment = (document.getElementById("de-rectif-comment") as HTMLTextAreaElement)?.value?.trim()
                    // Pas de select pour un créateur DG → cible « creator » par défaut.
                    const cible = ((document.getElementById("de-rectif-cible") as HTMLSelectElement)?.value ?? "creator") as "creator" | "dg"
                    if (!comment) {
                         Swal.showValidationMessage("Un commentaire est requis")
                         return false
                    }
                    return { comment, cible }
               },
          }).then(res => res.isConfirmed && res.value && runAction(personnelDERectifierHrRequest(d.id, res.value.comment, res.value.cible), "Rectification demandée.", d.id, "rectifierHr"))
     }

     function handleSupprimer(d: IPersonnelDemandeEmbaucheModel) {
          Swal.fire({
               icon: "warning",
               title: "Abandonner cette demande ?",
               text: "La demande et le collaborateur provisoire seront supprimés. Action irréversible.",
               showCancelButton: true,
               confirmButtonText: "Abandonner",
               cancelButtonText: "Annuler",
               confirmButtonColor: "#d33",
          }).then(res => res.isConfirmed && runAction(personnelDESupprimerRequest(d.id), "Demande abandonnée.", d.id, "supprimer"))
     }

     function openSuivi(d: IPersonnelDemandeEmbaucheModel) {
          setSuiviModal({ show: true, demande: d, loading: true })
          personnelDEGetDetailsRequest(d.id)
               .then(r => setSuiviModal({ show: true, demande: r.data, loading: false }))
               .catch(() => setSuiviModal({ show: true, demande: d, loading: false }))
     }

     const demandes = query.data ?? []

     return (
          <>
               <div className="card">
                    <div className="card-body pt-6">
                         {/* Bouton « Ajouter » pleine largeur (mobile) — la carte pointillée prend le relais sur desktop. */}
                         {canAdd && (
                              <Link
                                   to={ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_CREATE}
                                   className="btn btn-primary w-100 mb-5 d-sm-none d-flex align-items-center justify-content-center"
                              >
                                   <i className="fas fa-plus me-2" /> Ajouter un collaborateur
                              </Link>
                         )}
                         {query.isLoading ? (
                              <CenteredLoader minHeight="240px" />
                         ) : query.isError ? (
                              <div className="alert alert-danger mb-0">Erreur lors du chargement des demandes d'embauche.</div>
                         ) : demandes.length === 0 && !canAdd ? (
                              <div className="d-flex flex-column align-items-center text-center text-muted py-10">
                                   <i className="fas fa-inbox fs-3x mb-3 opacity-50" />
                                   <div className="fs-5">Aucune demande d'embauche en cours.</div>
                              </div>
                         ) : (
                              <div className="row g-4">
                                   {/* Carte « Ajouter » (desktop / tablette) — visible seulement avec la permission requise. */}
                                   {canAdd && (
                                        <div className="col-sm-6 col-xl-4 d-none d-sm-block">
                                             <Link
                                                  to={ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_CREATE}
                                                  className="card h-100 de-add-card d-flex flex-column align-items-center justify-content-center text-center text-decoration-none py-10"
                                             >
                                                  <span className="de-add-plus d-flex align-items-center justify-content-center rounded-circle mb-3">
                                                       <i className="fas fa-plus" />
                                                  </span>
                                                  <div className="fs-5 fw-bold text-primary">Ajouter un collaborateur</div>
                                                  <div className="text-muted fs-7 mt-1">Nouvelle demande d'embauche</div>
                                             </Link>
                                        </div>
                                   )}
                                   {demandes.map(d => {
                                        const etat = d.etat ?? ""
                                        const color = ETAT_COLOR[etat] ?? "secondary"
                                        // Ligne verrouillée pendant une action ; spinner sur le bouton de l'action en cours.
                                        const rowBusy = busy.id === d.id
                                        const spin = (action: string, label: React.ReactNode) =>
                                             rowBusy && busy.action === action ? (
                                                  <>
                                                       <span className="spinner-border spinner-border-sm me-1" role="status" /> {label}
                                                  </>
                                             ) : (
                                                  label
                                             )
                                        const isRectif = [ETAT_RECTIF_PAR_DG, ETAT_RECTIF_PAR_HR].includes(etat)
                                        const isOwner = d.requester?.id === currentUser?.personnel?.id
                                        const isNonSalarie = (d.personnel as any)?.statut === "STATUT_NON_SALARIE"
                                        // Édition alignée sur le backend `assertCanEdit` (chacun édite pendant son tour).
                                        const canEdit = isOwner
                                             ? [ETAT_BROUILLON, ETAT_RECTIF_PAR_DG, ETAT_RECTIF_PAR_HR].includes(etat)
                                             : isDg
                                               ? etat === ETAT_SOUMIS_DG
                                               : isHr
                                                 ? etat === ETAT_VALIDE_DG && !isNonSalarie && !d.creatorIsHr // non salarié / demande initiée par les RH : étape RH sautée
                                                 : false
                                        const canSoumettre = isOwner && [ETAT_BROUILLON, ETAT_RECTIF_PAR_DG, ETAT_RECTIF_PAR_HR].includes(etat)
                                        const canDg = etat === ETAT_SOUMIS_DG && canActDg
                                        // Validation RH : salarié uniquement (le non salarié va directement au super-admin après la DG).
                                        const canHr = etat === ETAT_VALIDE_DG && !d.creatorIsHr && !isNonSalarie && canActHr
                                        // Finalisation super-admin : après validation RH (salarié) ou dès validation DG (non salarié / créateur RH).
                                        const canFinalize = isSuperAdmin && (etat === ETAT_VALIDE_HR || (etat === ETAT_VALIDE_DG && (d.creatorIsHr || isNonSalarie)))
                                        const canDelete = etat !== ETAT_FINALISE && isOwner
                                        const collaborateur = formatPrenomNom(d.personnel?.prenom, d.personnel?.nom) || "—"
                                        const poste = d.personnel?.personnelPostes?.[0]?.poste?.nom
                                        const entite = d.personnel?.mainEntite?.nom
                                        const detailsPath = ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_DETAILS(d.id)
                                        return (
                                             <div className="col-12 col-sm-6 col-xl-4" key={d.id}>
                                                  <div className="card h-100 border border-gray-300 shadow-sm de-card">
                                                       <div className={`h-4px w-100 bg-${color} rounded-top`} />
                                                       <div className="card-body d-flex flex-column p-5">
                                                            {/* En-tête : avatar + nom */}
                                                            <div className="d-flex align-items-center gap-3 mb-3">
                                                                 <div className={`symbol symbol-45px symbol-circle`}>
                                                                      {(d.personnel as any)?.avatarGoogleDriveId ? (
                                                                           <img src={driveImageUrl((d.personnel as any).avatarGoogleDriveId)} alt={collaborateur} referrerPolicy="no-referrer" style={{ objectFit: "cover" }} />
                                                                      ) : (
                                                                           <span className={`symbol-label bg-light-${color} text-${color} fw-bold fs-5`}>
                                                                                {initials(d.personnel?.prenom, d.personnel?.nom)}
                                                                           </span>
                                                                      )}
                                                                 </div>
                                                                 <div className="min-w-0">
                                                                      <Link to={detailsPath} className="fs-5 fw-bold text-gray-900 text-hover-primary d-block text-truncate">
                                                                           {collaborateur}
                                                                      </Link>
                                                                      <div className="text-muted fs-7 text-truncate">{poste || "Poste non précisé"}</div>
                                                                 </div>
                                                            </div>

                                                            {/* État (sur sa propre ligne pour éviter tout débordement) */}
                                                            <div className="mb-3">
                                                                 <span className={`badge badge-light-${color} text-wrap text-start`}>{d.etatLabel ?? etat}</span>
                                                            </div>

                                                            {/* Méta */}
                                                            <div className="d-flex flex-column gap-2 mb-4">
                                                                 <div className="d-flex align-items-center text-gray-700 fs-7">
                                                                      <i className="fas fa-user-plus text-muted w-20px" />
                                                                      <span className="text-muted me-1">Demandeur :</span>
                                                                      <span className="fw-semibold text-truncate">{formatPrenomNom(d.requester?.prenom, d.requester?.nom) || "—"}</span>
                                                                 </div>
                                                                 {entite && (
                                                                      <div className="d-flex align-items-center text-gray-700 fs-7">
                                                                           <i className="fas fa-building text-muted w-20px" />
                                                                           <span className="text-muted me-1">Entité :</span>
                                                                           <span className="fw-semibold text-truncate">{entite}</span>
                                                                      </div>
                                                                 )}
                                                                 <div className="d-flex align-items-center text-gray-700 fs-7">
                                                                      <i className="fas fa-calendar-day text-muted w-20px" />
                                                                      <span className="text-muted me-1">Créée le :</span>
                                                                      <span className="fw-semibold">{d.dateDemande ? moment(d.dateDemande).format("DD/MM/YYYY") : "—"}</span>
                                                                 </div>
                                                            </div>

                                                            {/* Actions */}
                                                            <div className="d-flex flex-wrap gap-2 mt-auto pt-3 border-top border-gray-200">
                                                                 <Link className="btn btn-sm btn-light-primary flex-grow-1" to={detailsPath}>
                                                                      <i className="fas fa-eye me-1" /> Détails
                                                                 </Link>
                                                                 <button className="btn btn-sm btn-light flex-grow-1" onClick={() => openSuivi(d)}>
                                                                      <i className="fas fa-history me-1" /> Suivi
                                                                 </button>
                                                                 {canEdit && (
                                                                      <Link className="btn btn-sm btn-light-warning flex-grow-1" to={ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_EDITER(d.id)}>
                                                                           <i className="fas fa-pen me-1" /> {isOwner ? (etat === ETAT_BROUILLON ? "Compléter" : "Corriger") : "Éditer"}
                                                                      </Link>
                                                                 )}
                                                                 {canSoumettre && (
                                                                      <button className="btn btn-sm btn-primary flex-grow-1" disabled={rowBusy} onClick={() => handleSoumettre(d)}>
                                                                           {spin("soumettre", isRectif ? "Envoyer" : "Soumettre")}
                                                                      </button>
                                                                 )}
                                                                 {canDg && (
                                                                      <>
                                                                           <button className="btn btn-sm btn-success flex-grow-1" disabled={rowBusy} onClick={() => handleValiderDg(d)}>
                                                                                {spin("validerDg", d.isDgReexamen ? "Revalider" : "Valider")}
                                                                           </button>
                                                                           <button className="btn btn-sm btn-warning flex-grow-1" disabled={rowBusy} onClick={() => handleRectifierDg(d)}>
                                                                                {spin("rectifierDg", "Demander une rectification")}
                                                                           </button>
                                                                      </>
                                                                 )}
                                                                 {canHr && (
                                                                      <>
                                                                           <button className="btn btn-sm btn-success flex-grow-1" disabled={rowBusy} onClick={() => handleValiderHr(d)}>
                                                                                {spin("validerHr", "Valider")}
                                                                           </button>
                                                                           <button className="btn btn-sm btn-warning flex-grow-1" disabled={rowBusy} onClick={() => handleRectifierHr(d)}>
                                                                                {spin("rectifierHr", "Demander une rectification")}
                                                                           </button>
                                                                      </>
                                                                 )}
                                                                 {canFinalize && (
                                                                      <button className="btn btn-sm btn-dark flex-grow-1" disabled={rowBusy} onClick={() => navigate(ROUTING_ABSOLUTE_PATH_GESTION_STRUCTURE_STAFF_DEMANDE_EMBAUCHE_FINALISER(d.id))}>
                                                                           <i className="fas fa-check-double me-1" /> Finaliser
                                                                      </button>
                                                                 )}
                                                                 {canDelete && (
                                                                      <button className="btn btn-sm btn-light-danger" disabled={rowBusy} onClick={() => handleSupprimer(d)} title="Abandonner">
                                                                           {spin("supprimer", <i className="fas fa-trash" />)}
                                                                      </button>
                                                                 )}
                                                            </div>
                                                       </div>
                                                  </div>
                                             </div>
                                        )
                                   })}
                              </div>
                         )}
                    </div>
               </div>

               {suiviModal.show && (
                    <MyModal title={"Historique de la demande"} show={suiviModal.show} onCloseCallback={() => setSuiviModal({ show: false, loading: false })}>
                         {suiviModal.loading ? (
                              <CenteredLoader minHeight="200px" />
                         ) : (
                              <div className="timeline">
                                   {(suiviModal.demande?.suivis ?? []).length === 0 ? (
                                        <div className="text-muted">Aucun événement.</div>
                                   ) : (
                                        [...(suiviModal.demande?.suivis ?? [])].reverse().map(s => (
                                             <div className="d-flex align-items-start mb-4" key={s.id}>
                                                  <span className="bullet bullet-vertical h-40px bg-primary me-3" />
                                                  <div>
                                                       <div className="fw-bold">{s.typeLabel ?? s.type}</div>
                                                       <div className="text-muted fs-7">
                                                            {s.date ? moment(s.date).format("DD/MM/YYYY HH:mm") : ""}
                                                            {s.personnel ? ` — ${formatPrenomNom(s.personnel.prenom, s.personnel.nom) || s.personnel.prenomNom || ""}` : ""}
                                                       </div>
                                                       {s.commentaire && <div className="mt-1 p-2 bg-light-warning rounded fs-7">{s.commentaire}</div>}
                                                  </div>
                                             </div>
                                        ))
                                   )}
                              </div>
                         )}
                    </MyModal>
               )}
          </>
     )
}

export default DemandesEmbaucheStaffListing
